import { useQuery } from "@tanstack/react-query";
import { api } from "./api";

/**
 * Athlete insights feed.
 *
 * Shape mirrors the EventsFeed component's PatternEvent contract so
 * the page can pass `data?.events` straight through without a mapper.
 */

export type InsightSeverity = "ok" | "info" | "watch" | "alert";

export interface InsightEvent {
  key: string;
  label: string;
  summary: string;
  severity: InsightSeverity;
  is_new: boolean;
  knowledge_link: string;
  level: 1 | 2 | 3 | 4;
  confidence: "high" | "medium" | "low";
  safety_critical: boolean;
}

export interface InsightsFeed {
  events: InsightEvent[];
  total_evaluated: number;
}

export interface UseInsightsFeedOptions {
  lang?: string;
  includeOptimal?: boolean;
  limit?: number;
}

export function useInsightsFeed(opts: UseInsightsFeedOptions = {}) {
  const { lang, includeOptimal = false, limit = 7 } = opts;
  return useQuery<InsightsFeed>({
    queryKey: ["athlete-insights-feed", lang ?? "ru", includeOptimal, limit],
    queryFn: async () => {
      const params: Record<string, string | number | boolean> = { limit };
      if (lang) params.lang = lang;
      if (includeOptimal) params.include_optimal = true;
      const r = await api.get<InsightsFeed>("/athlete/insights/feed", { params });
      return r.data;
    },
    // Pattern matcher is deterministic over the day's data; refetching
    // every 5 minutes is enough to catch new wellness syncs.
    staleTime: 5 * 60_000,
  });
}
